import React from 'react';
import { Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';

type VisualizerLinkProps = {
  gridId?: string | number;
  observationId?: string;
  projectId?: string | number;
  reportId?: string | number;
  children: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
};

const VisualizerLink: React.FC<VisualizerLinkProps> = ({
  gridId,
  observationId,
  projectId,
  reportId,
  children,
  disabled = false,
  disabledReason,
}) => {
  const isObservationLink = observationId != null;
  const isGridLink = !isObservationLink && gridId != null;
  const hasRequiredObservationParams = gridId != null && observationId != null && projectId != null && reportId != null;
  const isEffectivelyDisabled = disabled || (!isGridLink && !hasRequiredObservationParams);
  const disabledTooltipTitle = isEffectivelyDisabled
    ? disabledReason ||
      (isObservationLink && !hasRequiredObservationParams
        ? 'Missing project/report/observation for visualizer link.'
        : 'Visualizer link is disabled.')
    : '';

  const handleLinkClick = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    if (isEffectivelyDisabled) {
      return;
    }

    // Open a temporary tab immediately
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      if (isGridLink) {
        const encodedGridId = encodeURIComponent(String(gridId));
        newWindow.location.href = `/visualizer/?grid_id=${encodedGridId}`;
        return;
      }

      const encodedGridId = encodeURIComponent(String(gridId));
      const encodedProjectId = encodeURIComponent(String(projectId));
      const encodedReportId = encodeURIComponent(String(reportId));
      const encodedObservationId = encodeURIComponent(String(observationId));
      newWindow.location.href = `/visualizer/?grid_id=${encodedGridId}&project_id=${encodedProjectId}&report_id=${encodedReportId}&observation_id=${encodedObservationId}`;
    }
  };

  return (
    <>
      <Tooltip title={disabledTooltipTitle} disableHoverListener={!isEffectivelyDisabled}>
        <span
          style={{
            display: 'inline-block',
            cursor: isEffectivelyDisabled ? 'not-allowed' : 'pointer',
            opacity: isEffectivelyDisabled ? 0.5 : 1,
          }}
        >
          <Link
            to="#"
            onClick={handleLinkClick}
            aria-disabled={isEffectivelyDisabled}
            tabIndex={isEffectivelyDisabled ? -1 : 0}
            style={{
              pointerEvents: isEffectivelyDisabled ? 'none' : 'auto',
              color: isEffectivelyDisabled ? '#9e9e9e' : 'inherit',
              textDecoration: 'none',
            }}
          >
            {children}
          </Link>
        </span>
      </Tooltip>
    </>
  );
};

export default VisualizerLink;
